import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EconomyOverview } from '@galaxis/shared';
import { fetchEconomyOverview, acknowledgeEconomy } from '../api/galaxy.ts';

/** Shown at most once after login when meaningful time passed since the last visit. */
const MIN_AWAY_MS = 10 * 60_000;
const ACK_INTERVAL_MS = 2 * 60_000;

export function WelcomeBack() {
  const [summary, setSummary] = useState<EconomyOverview['sinceLastVisit']>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEconomyOverview()
      .then(ov => {
        if (cancelled) return;
        const s = ov.sinceLastVisit;
        if (s && Date.now() - new Date(s.since).getTime() >= MIN_AWAY_MS && (s.produced.metal > 0 || s.produced.energy > 0)) {
          setSummary(s);
        } else {
          void acknowledgeEconomy().catch(() => undefined);
        }
      })
      .catch(() => undefined);
    // While playing, keep moving the "last visit" mark forward
    const t = setInterval(() => { void acknowledgeEconomy().catch(() => undefined); }, ACK_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!summary) return null;
  const close = () => { setSummary(null); void acknowledgeEconomy().catch(() => undefined); };
  const hours = (Date.now() - new Date(summary.since).getTime()) / 3_600_000;
  const away = hours >= 1 ? `${Math.floor(hours)} óra ${Math.round((hours % 1) * 60)} perc` : `${Math.round(hours * 60)} perc`;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={close}>
      <div role="dialog" aria-modal="true" aria-label="Amíg távol voltál"
        className="sci-fi-panel w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4"
        onClick={e => e.stopPropagation()}>
        <h2 className="text-base font-semibold text-slate-100">Amíg távol voltál ({away})</h2>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-slate-400">Termelt fém</dt><dd className="font-mono text-emerald-400 text-right">+{summary.produced.metal.toLocaleString('en-US')}</dd>
          <dt className="text-slate-400">Termelt energia</dt><dd className="font-mono text-emerald-400 text-right">+{summary.produced.energy.toLocaleString('en-US')}</dd>
          <dt className="text-slate-400">Elköltött fém</dt><dd className="font-mono text-slate-300 text-right">−{summary.spent.metal.toLocaleString('en-US')}</dd>
          <dt className="text-slate-400">Elköltött energia</dt><dd className="font-mono text-slate-300 text-right">−{summary.spent.energy.toLocaleString('en-US')}</dd>
        </dl>
        <button type="button" className="btn-primary w-full text-sm" onClick={close}>Tovább</button>
      </div>
    </div>,
    document.body,
  );
}
